import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IConfigurations, defaultValue } from 'app/shared/model/configurations.model';

const initialState: EntityState<IConfigurations> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IConfigurations>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/school-configs';

// Actions

export const getSchoolConfigs = createAsyncThunk('schoolConfig/fetch_schoolConfig_list', async (schoolId: string | number) => {
  const requestUrl = `${apiUrl}/school/${schoolId}`;
  return axios.get<IConfigurations[]>(requestUrl);
});

export const getSchoolConfig = createAsyncThunk(
  'schoolConfig/fetch_schoolConfig',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IConfigurations>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const updateSchoolConfig = createAsyncThunk(
  'schoolConfig/update_schoolConfig',
  async (entity: IConfigurations, thunkAPI) => {
    const result = await axios.put<IConfigurations>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getSchoolConfigs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type SchoolConfigState = Readonly<typeof initialState>;

// slice

export const SchoolConfigSlice = createSlice({
  name: 'schoolConfig',
  initialState: initialState as SchoolConfigState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSchoolConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(updateSchoolConfig), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getSchoolConfigs), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isPending(getSchoolConfigs, getSchoolConfig, updateSchoolConfig), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      });
  },
});

export const { reset } = SchoolConfigSlice.actions;

// Reducer
export default SchoolConfigSlice.reducer;
